import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Typography, Tag, Row, Col, Avatar, Button } from 'antd';
import { Link } from 'react-router-dom';
import { 
    BookOutlined, 
    CodeOutlined, 
    ExperimentOutlined, 
    CalculatorOutlined,
    GlobalOutlined,
    BulbOutlined,
    RocketOutlined,
    HeartOutlined,
    StarOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';

const { Title, Paragraph } = Typography;

// 课程图标映射
const courseIcons = {
    1: <BookOutlined />,
    2: <CodeOutlined />,
    3: <ExperimentOutlined />,
    4: <CalculatorOutlined />,
    5: <GlobalOutlined />,
    6: <BulbOutlined />,
    7: <RocketOutlined />,
    8: <HeartOutlined />,
    default: <StarOutlined />
};

// 课程颜色主题
const courseColors = [
    '#1890ff', '#52c41a', '#faad14', '#f5222d', 
    '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'
];

const CourseSelection = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                console.log('API URL:', 'https://api.galaxymentor.com/all-courses');

                const response = await fetch(`https://api.galaxymentor.com/all-courses`, {
                    method: 'GET',
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                console.log('Received data:', data);
                
                if (Array.isArray(data)) {
                    const sortedCourses = data.sort((a, b) => parseInt(a.courseid) - parseInt(b.courseid));
                    setCourses(sortedCourses);
                } else {
                    throw new Error('Invalid data format received');
                }
            } catch (error) {
                console.error('Error fetching courses:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, []);

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '60vh',
                flexDirection: 'column'
            }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: '#666' }}>Loading courses...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '40px 20px', maxWidth: 600, margin: '0 auto' }}>
                <Alert
                    message="Error Loading Courses"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    if (courses.length === 0) {
        return (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <Alert
                    message="No Courses Available"
                    description="There are currently no courses available. Please check back later."
                    type="info"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '40px 20px', 
            background: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)'
        }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                    <Title level={1} style={{ color: '#1890ff', marginBottom: 8 }}>
                        🌟 Galaxy Mentor Courses
                    </Title>
                    <Paragraph style={{ color: '#666', fontSize: 18 }}>
                        Explore our comprehensive learning paths designed to accelerate your knowledge
                    </Paragraph>
                </div>

                <Row gutter={[24, 24]}>
                    {courses.map((course, index) => {
                        const courseIcon = courseIcons[course.courseid] || courseIcons.default;
                        const courseColor = courseColors[index % courseColors.length];
                        
                        return (
                            <Col xs={24} sm={12} lg={8} xl={6} key={course.courseid}>
                                <Card
                                    hoverable
                                    className="course-card"
                                    style={{
                                        height: '100%',
                                        borderRadius: 16,
                                        overflow: 'hidden',
                                        border: 'none',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ padding: 0 }}
                                    cover={
                                        <div style={{
                                            background: `linear-gradient(135deg, ${courseColor}22, ${courseColor}44)`,
                                            padding: '30px 20px',
                                            textAlign: 'center',
                                            borderBottom: `3px solid ${courseColor}`
                                        }}>
                                            <Avatar 
                                                size={64} 
                                                icon={courseIcon}
                                                style={{ 
                                                    backgroundColor: courseColor,
                                                    marginBottom: 16
                                                }}
                                            />
                                            <Title level={4} style={{ 
                                                margin: 0, 
                                                color: courseColor,
                                                fontSize: 16,
                                                fontWeight: 600
                                            }}>
                                                {course.course_title || `Course ${course.courseid}`}
                                            </Title>
                                        </div>
                                    }
                                >
                                    <div style={{ padding: '20px' }}>
                                        <Paragraph 
                                            style={{ 
                                                color: '#666',
                                                fontSize: 14,
                                                lineHeight: 1.5,
                                                marginBottom: 16,
                                                minHeight: 60
                                            }}
                                            ellipsis={{ rows: 3 }}
                                        >
                                            {course.course_description || course.course_desription || 'Comprehensive course content designed to enhance your understanding and skills.'}
                                        </Paragraph>

                                        <div style={{ marginBottom: 16 }}>
                                            <Tag color={courseColor} style={{ borderRadius: 12 }}>
                                                Interactive Learning
                                            </Tag>
                                            <Tag color="default" style={{ borderRadius: 12 }}>
                                                Self-Paced
                                            </Tag>
                                        </div>

                                        <Link to={`/course/${course.courseid}`} style={{ textDecoration: 'none' }}>
                                            <Button 
                                                type="primary" 
                                                block 
                                                size="large"
                                                icon={<PlayCircleOutlined />}
                                                style={{
                                                    backgroundColor: courseColor,
                                                    borderColor: courseColor,
                                                    borderRadius: 8,
                                                    height: 44,
                                                    fontSize: 16,
                                                    fontWeight: 500
                                                }}
                                            >
                                                Start Learning
                                            </Button>
                                        </Link>
                                    </div>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </div>
        </div>
    );
};

export default CourseSelection;